<template>
  <div class="chat-box fadeInUp" v-if="isVisible">
    <div class="chat-box-content">
      <div id="btn-close-chat" @click="close">
        <div>
          <svg fill="#ffffff" height="18" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
          </svg>
        </div>
      </div>
      
      <div class="box-header">
        <div style="width:75px" class="pull-right avatar">
          <span title="" data-container="body" rel="tooltip">
            <img 
              src="https://cdn.goftino.com/profile/60bc613f18bccb0585eecc98vdju.jpg" 
              alt="avatar" 
              width="40" 
              height="40"
            >
          </span>
        </div>
        <div class="pull-right box-title">
          <div class="title-name">{{ product?.username || '' }}</div>
          <div class="title-text">
            {{ $t('chatBox.chat_with', { type: product?.adType === 'seller' ? $t('chatBox.seller') : $t('chatBox.buyer') }) }}
          </div>
        </div>
      </div>
      
      <div class="box-body dragandrophandler">
        <div id="chatbox">
          <div class="dc-msg slideInleft left">
            <div class="dc-text" rel="tooltip">{{ $t('chatBox.default_question') }}</div>
            <div class="timeago_r slideInleft">{{ $t('chatBox.moments_ago') }}</div>
          </div>
          
          <!-- Display existing messages -->
          <div 
            v-for="message in messages" 
            :key="message.id"
            :class="'dc-msg slideIn' + (message.sender_id === currentUserId ? 'right right' : 'left left')"
          >
            <div class="dc-text" rel="tooltip">{{ message.message }}</div>
            <div class="timeago_r slideInslideInleft">{{ $t('chatBox.moments_ago') }}</div>
          </div>
        </div>
      </div>
      
      <div class="box-footer">
        <form id="box-send" class="hasmic" @submit.prevent="sendMessage">
          <div class="form-group is-empty">
            <textarea 
              maxlength="2000" 
              id="message_box" 
              type="text" 
              name="message"
              :placeholder="$t('chatBox.write_message')" 
              class="form-control"
              v-model="newMessage"
            ></textarea>
            <span class="material-input"></span>
          </div>
          <div class="fix-content">
            <button type="submit" class="btn btn-simple btn-send">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="23" height="23" fill="rgb(242,100,61)">
                <path d="M22,3,1,12l21,9V14L7,12l15-2Z"></path>
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const { t } = useI18n()

// Props
const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

// Emits
const emit = defineEmits(['close'])

// State
const isVisible = ref(true)
const newMessage = ref('')
const messages = ref([])
const currentUserId = ref(getMockUserId())

// Methods
const close = () => {
  isVisible.value = false
  emit('close')
}

const sendMessage = async () => {
  if (!newMessage.value.trim()) return
  
  try {
    // Add message to UI immediately for better UX
    const messageObj = {
      id: Date.now(),
      message: newMessage.value,
      sender_id: currentUserId.value,
      receiver_id: props.product.userId,
      timestamp: new Date()
    }
    
    messages.value.push(messageObj)
    
    // Send to API
    const response = await $fetch('/market/api/messages/send', {
      method: 'POST',
      body: {
        chat_id: props.product.id,
        sender_id: currentUserId.value,
        receiver_id: props.product.userId,
        message: newMessage.value,
        _token: 'csrf_token' // In a real app, get from auth context
      }
    })
    
    // Update with server response if needed
    if (response.chat_id) {
      // Store the returned chat_id for future messages
    }
    
    // Clear input
    newMessage.value = ''
  } catch (error) {
    console.error('Error sending message:', error)
    
    // Show error to user
    alert(t('chatBox.send_error'))
  }
}

// Mock function to get user ID - replace with actual auth
function getMockUserId() {
  return 1
}
</script>

<style lang="scss" scoped>
.chat-box {
  position: fixed;
  bottom: 70px;
  right: 15px;
  width: 320px;
  max-width: 80vw;
  max-height: 450px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  background: white;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  
  .chat-box-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    
    #btn-close-chat {
      position: absolute;
      top: 10px;
      left: 10px;
      color: #666;
      cursor: pointer;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      
      &:hover {
        background: rgba(0, 0, 0, 0.2);
      }
    }
    
    .box-header {
      background: #f0f0f0;
      padding: 15px;
      border-radius: 12px 12px 0 0;
      display: flex;
      align-items: center;
      
      .avatar img {
        border-radius: 50%;
      }
      
      .box-title {
        margin-right: 10px;
        
        .title-name {
          font-weight: bold;
          margin-bottom: 2px;
          color: #333;
        }
        
        .title-text {
          font-size: 12px;
          color: #666;
        }
      }
    }
    
    .box-body {
      flex: 1;
      overflow-y: auto;
      padding: 15px;
      
      .dc-msg {
        margin-bottom: 10px;
        
        &.left {
          text-align: right;
          
          .dc-text {
            background: #e4e6eb;
            color: #000;
            border-radius: 18px 18px 18px 4px;
          }
        }
        
        &.right {
          text-align: left;
          
          .dc-text {
            background: #03a054;
            color: #fff;
            border-radius: 18px 18px 4px 18px;
          }
        }
        
        .dc-text {
          padding: 8px 12px;
          display: inline-block;
          max-width: 75%;
          word-wrap: break-word;
        }
        
        .timeago_r {
          font-size: 11px;
          color: #999;
          margin-top: 4px;
        }
      }
    }
    
    .box-footer {
      padding: 15px;
      border-top: 1px solid #eee;
      
      form {
        display: flex;
        align-items: flex-end;
        
        .form-group {
          flex: 1;
          margin-left: 10px;
          
          textarea {
            width: 100%;
            border: 1px solid #ddd;
            border-radius: 18px;
            padding: 8px 12px;
            font-size: 14px;
            resize: none;
            min-height: 36px;
            max-height: 100px;
            
            &:focus {
              outline: none;
              border-color: #03a054;
            }
          }
        }
        
        .btn-send {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #f42642;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          
          &:hover {
            background: #e64942;
          }
        }
      }
    }
  }
}

// Animations
.fadeInUp {
  animation: fadeInUp 0.3s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slideInleft, .slideInright {
  animation-duration: 0.3s;
  animation-fill-mode: both;
}

.slideInleft {
  animation-name: slideInLeft;
}

.slideInright {
  animation-name: slideInRight;
}

@keyframes slideInLeft {
  from {
    transform: translate3d(-100%, 0, 0);
    visibility: visible;
  }
  to {
    transform: translate3d(0, 0, 0);
  }
}

@keyframes slideInRight {
  from {
    transform: translate3d(100%, 0, 0);
    visibility: visible;
  }
  to {
    transform: translate3d(0, 0, 0);
  }
}
</style>